import { useState, useEffect, useRef, useContext } from 'react';
import { useWidgetStore, WidgetOrchestratorContext } from '../../state/createWidgetStore';
import { t } from '../../i18n';
import { McpForm } from '../form/McpForm';

export function ConfirmationCard({ messageId }: { messageId: string }) {
  const pendingConfirmation = useWidgetStore((s) => s.pendingConfirmation);
  const isStreaming = useWidgetStore((s) => s.messages.find((m) => m.id === messageId)?.isStreaming ?? false);
  const orchestratorRef = useContext(WidgetOrchestratorContext);
  const [step, setStep] = useState<'approve' | 'params'>('approve');
  const [resolved, setResolved] = useState<'approved' | 'rejected' | null>(null);
  const lastPcRef = useRef<typeof pendingConfirmation>(null);
  const streamingStarted = useRef(false);

  // Dismiss the badge once the tool response finishes streaming
  useEffect(() => {
    if (!resolved) {
      streamingStarted.current = false;
      return;
    }
    if (isStreaming) {
      streamingStarted.current = true;
    } else if (streamingStarted.current) {
      setResolved(null);
      streamingStarted.current = false;
    }
  }, [resolved, isStreaming]);

  if (pendingConfirmation?.messageId === messageId) {
    lastPcRef.current = pendingConfirmation;
  }

  const pc = pendingConfirmation?.messageId === messageId ? pendingConfirmation : null;

  if (!pc && !resolved) return null;

  if (resolved) {
    const stored = lastPcRef.current;
    return (
      <div className={`confirmation-card ${resolved === 'approved' ? 'confirmed' : 'rejected'}`}>
        <div className="confirmation-header">
          <span className="confirmation-icon">⚠</span>
          <span>{resolved === 'approved' ? t('confirmationApproved') : t('confirmationRejected')}</span>
        </div>
        {stored && resolved === 'approved' && (
          <div className="tool-call-details">
            {Object.entries(stored.toolArgs)
              .filter(([, v]) => v !== undefined && v !== null && v !== '')
              .map(([k, v]) => {
                const field = stored.fields?.find(f => f.name === k);
                const label = field?.label || k;
                const option = field?.options?.find(o => o.value === String(v));
                const display = option
                  ? option.label
                  : field?.type === 'checkbox'
                  ? (v ? 'Yes' : 'No')
                  : JSON.stringify(v);
                return `${label}: ${display}`;
              })
              .join(' | ')}
          </div>
        )}
      </div>
    );
  }

  if (!pc) return null;

  const warnIcon = (
    <svg className="confirmation-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
      <line x1="12" y1="9" x2="12" y2="13"/>
      <line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
  );

  const handleApprove = () => {
    const hasEmptyRequired = pc.fields.some(
      f => f.required && (pc.toolArgs[f.name] === undefined || pc.toolArgs[f.name] === null || pc.toolArgs[f.name] === ''),
    );
    if (pc.formEnabled !== false && hasEmptyRequired) {
      setStep('params');
    } else {
      setResolved('approved');
      orchestratorRef?.current?.resolveConfirmation(true);
    }
  };

  const handleReject = () => {
    setResolved('rejected');
    orchestratorRef?.current?.resolveConfirmation(false);
  };

  if (step === 'approve') {
    return (
      <div className="confirmation-card">
        <div className="confirmation-header">
          {warnIcon}
          <span>{t('confirmationTitle').replace('{tool}', pc.toolName)}</span>
        </div>
        {pc.toolDescription && (
          <div className="confirmation-desc">{pc.toolDescription}</div>
        )}
        <div className="form-actions" style={{ marginTop: 8 }}>
          <button className="form-submit" onClick={handleApprove}>{t('formApprove')}</button>
          <button className="form-cancel" onClick={handleReject}>{t('formReject')}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="confirmation-card">
      <div className="confirmation-header">
        {warnIcon}
        <span>{t('confirmationParamsTitle').replace('{tool}', pc.toolName)}</span>
      </div>
      <McpForm
        fields={pc.fields}
        initialValues={pc.toolArgs}
        submitLabel={t('formSubmit')}
        cancelLabel={t('formCancel')}
        onCancel={() => setStep('approve')}
        onSubmit={(values) => {
          setResolved('approved');
          orchestratorRef?.current?.resolveConfirmation(true, { ...pc.toolArgs, ...values });
        }}
        formType="confirmation"
      />
    </div>
  );
}
